/**
 * Purchasing, transfers and stock counts (docs/BUILD-PHASES.md Phase 7).
 */
import { z } from 'zod';
import { idSchema, paisaSchema, positiveQtyMilliSchema, bpsSchema } from './common.js';
/* -------------------------------------------------------------------------- */
/* Suppliers (Party, kind SUPPLIER)                                           */
/* -------------------------------------------------------------------------- */
export const upsertSupplierSchema = z.object({
    name: z.string().min(1).max(200),
    phone: z.string().max(20).optional(),
    email: z.string().email().max(200).optional(),
    address: z.string().max(500).optional(),
    ntn: z.string().max(20).optional(),
    strn: z.string().max(20).optional(),
    creditDays: z.number().int().min(0).max(365).default(0),
    isActive: z.boolean().default(true),
});
export const recordSupplierPaymentSchema = z.object({
    shopId: idSchema,
    goodsReceiptId: idSchema.optional(),
    amountPaisa: paisaSchema.min(1),
    method: z.enum(['CASH', 'BANK_TRANSFER', 'WALLET', 'CHEQUE', 'CARD']),
    reference: z.string().trim().max(120).optional(),
    note: z.string().trim().max(300).optional(),
});
export const listSuppliersQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
    search: z.string().max(200).optional(),
    activeOnly: z.coerce.boolean().optional(),
});
/* -------------------------------------------------------------------------- */
/* Purchase orders                                                             */
/* -------------------------------------------------------------------------- */
export const purchaseOrderLineSchema = z.object({
    variantId: idSchema,
    qtyMilli: positiveQtyMilliSchema,
    unitCostPaisa: paisaSchema.min(0),
    taxRateBps: bpsSchema.default(0),
});
export const createPurchaseOrderSchema = z.object({
    shopId: idSchema,
    supplierId: idSchema,
    expectedAt: z.coerce.date().optional(),
    note: z.string().max(500).optional(),
    lines: z.array(purchaseOrderLineSchema).default([]),
});
export const updatePurchaseOrderLinesSchema = z.object({
    lines: z.array(purchaseOrderLineSchema).min(1),
});
export const listPurchaseOrdersQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
    shopId: idSchema.optional(),
    supplierId: idSchema.optional(),
    status: z.string().optional(),
});
/* -------------------------------------------------------------------------- */
/* Goods receipts (GRN)                                                        */
/* -------------------------------------------------------------------------- */
export const goodsReceiptLineSchema = z.object({
    variantId: idSchema,
    batchNo: z.string().max(60).optional(),
    expiryDate: z.coerce.date().optional(),
    qtyMilli: positiveQtyMilliSchema,
    freeQtyMilli: z.number().int().min(0).default(0),
    unitCostPaisa: paisaSchema.min(0),
    discountPaisa: paisaSchema.min(0).default(0),
    taxRateBps: bpsSchema.default(0),
});
export const createGoodsReceiptSchema = z.object({
    shopId: idSchema,
    supplierId: idSchema,
    purchaseOrderId: idSchema.optional(),
    allowOverReceipt: z.boolean().default(false),
    supplierInvoiceNo: z.string().max(60).optional(),
    supplierInvoiceDate: z.coerce.date().optional(),
    otherChargesPaisa: paisaSchema.min(0).default(0),
    note: z.string().max(500).optional(),
    lines: z.array(goodsReceiptLineSchema).min(1),
});
export const listGoodsReceiptsQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
    shopId: idSchema.optional(),
    supplierId: idSchema.optional(),
    status: z.string().optional(),
});
/* -------------------------------------------------------------------------- */
/* Purchase returns                                                            */
/* -------------------------------------------------------------------------- */
export const purchaseReturnLineSchema = z.object({
    variantId: idSchema,
    // Required for a batch-tracked variant — the specific batch this shop's
    // stock is coming off (validated server-side against that variant/shop).
    batchId: idSchema.optional(),
    qtyMilli: positiveQtyMilliSchema,
    unitCostPaisa: paisaSchema.min(0),
    taxRateBps: bpsSchema.default(0),
});
export const createPurchaseReturnSchema = z.object({
    shopId: idSchema,
    supplierId: idSchema,
    reason: z.string().max(300).optional(),
    lines: z.array(purchaseReturnLineSchema).min(1),
});
export const listPurchaseReturnsQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
    shopId: idSchema.optional(),
});
/* -------------------------------------------------------------------------- */
/* Stock transfers                                                             */
/* -------------------------------------------------------------------------- */
export const transferLineSchema = z.object({
    variantId: idSchema,
    // Required for a batch-tracked variant — the SOURCE shop's batch this line
    // draws from; `receive()` finds-or-creates the matching batch at the
    // destination shop from this batch's own batchNo/expiryDate.
    batchId: idSchema.optional(),
    qtyMilli: positiveQtyMilliSchema,
});
export const createTransferSchema = z
    .object({
    fromShopId: idSchema,
    toShopId: idSchema,
    note: z.string().max(500).optional(),
    lines: z.array(transferLineSchema).min(1),
})
    .refine((v) => v.fromShopId !== v.toShopId, { message: 'Cannot transfer a shop to itself' });
export const receiveTransferLineSchema = z.object({
    lineId: idSchema,
    receivedMilli: z.number().int().min(0),
    discrepancyNote: z.string().max(300).optional(),
});
export const receiveTransferSchema = z.object({
    lines: z.array(receiveTransferLineSchema).min(1),
});
export const listTransfersQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
    shopId: idSchema.optional(),
    status: z.string().optional(),
});
/* -------------------------------------------------------------------------- */
/* Stock counts                                                                */
/* -------------------------------------------------------------------------- */
export const createStockCountSchema = z.object({
    shopId: idSchema,
    scopeNote: z.string().max(200).optional(),
    variantIds: z.array(idSchema).min(1),
});
export const submitCountLinesSchema = z.object({
    lines: z
        .array(z.object({
        lineId: idSchema,
        countedMilli: z.number().int().min(0),
        reason: z.string().max(200).optional(),
    }))
        .min(1),
});
export const listStockCountsQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
    shopId: idSchema.optional(),
    status: z.string().optional(),
});
//# sourceMappingURL=purchasing.js.map